/*
 * Copyright (C) 2019 - present by Potentially
 *
 * Please see distribution for license.
 */

import { EditorContent, QuestionEditor, ReportMode, ReportSection, Resource, ResourceTag } from '..';
import { LearnerResourceProgress } from '../../../page-modules/resource/models';
import { DynamicContentRequest } from '../page/page.model';

/** Diagnostics card about state. */
export interface Diagnostics extends Resource {
  questions: DiagnosticQuestionResponse[];
  report: {
    title: LanguageValue[];
    showTitle: boolean;
    showLogo: boolean;
    sections: ReportSection[];
    reportMode: ReportMode;
  };
  logic?: {
    learningGoals: DiagnosticLogic[];
  };
  uri?: string;
  retakeCriteria: DiagnosticsRetake;
  feedbackProviderSettings?: FeedbackProviderSettings;
  filteredTags?: ResourceTag[];
  randomizeQuestions?: boolean;
}

export interface DiagnosticsWithProgress {
  card: Diagnostics;
  progress: LearnerResourceProgress;
}

export interface DiagnosticFormattedQuestion extends DiagnosticQuestionResponse {
  instruction?: LanguageValue;
  range?: string;
  value: LanguageValue;
  title: string;
  providerTitle?: string;
  options?: DiagnosticQuestionOptionWithSelection[];
  choices: DiagnosticQuestionOptionRequest[];
}

export interface CustomDiagnosticFormattedQuestion {
  data: DiagnosticFormattedQuestion;
  currentIndex: number;
}

export interface DiagnosticQuestionResponse {
  uid?: string;
  type: DiagnosticQuestionType;
  values?: LanguageValue[];
  instructions?: LanguageValue[];
  feedbackProviderInstructions?: LanguageValue[];
  description?: EditorContent[];
  feedbackProviderDescription?: EditorContent[];
  selectionLimit: number;
  optionsPerRow: number;
  allowEvidence?: boolean;
  feedback: EditorContent[];
  options?: DiagnosticQuestionOptionResponse[];
  questionComposition?: QuestionComposition;
  tags?: ResourceTag[];
  required?: boolean;
  showScore?: boolean;
  slideStepBy?: number;
  questionTarget?: DiagnosticQuestionTarget;
  listTitle?: string;
  showListTitle?: boolean;
  listType?: DiagnosticListQuestionListType;
  presentationMode?: DiagnosticListQuestionPresentationMode;
  max?: number;
  min?: number;
}

export interface DiagnosticQuestionTarget {
  feedbackProvider: boolean;
  feedbackReceiver: boolean;
}

export interface DiagnosticDescriptionQuestionResponse {
  item?: DiagnosticQuestionResponse;
  uploadUrls: string[];
}

export interface DiagnosticQuestionOptionResponse {
  uid: string;
  type: DiagnosticQuestionOptionType;
  score: number;
  correct?: boolean;
  deselectOthers?: boolean;
  direction: 'LEFT' | 'RIGHT' | 'NONE';
  instructions?: EditorContent[];
  values?: LanguageValue[];
}

export interface DiagnosticQuestionOptionWithSelection extends DiagnosticQuestionOptionResponse {
  title?: string;
  selected?: boolean;
}

export interface QuestionComposition {
  questions: DiagnosticQuestionResponse[];
  options?: DiagnosticQuestionOptionResponse[];
}

export interface DiagnosticQuestionRequest {
  uid?: string;
  type: DiagnosticQuestionType;
  value?: LanguageValue;
  description?: QuestionEditor;
  feedbackProviderDescription?: QuestionEditor;
  instruction?: LanguageValue;
  feedbackProviderInstructions?: LanguageValue;
  selectionLimit: number;
  optionsPerRow: number;
  required: boolean;
  options?: DiagnosticQuestionOptionRequest[];
  tags?: ResourceTag[] | string[];
  feedback?: EditorContent[];
  feedbackQuestion?: boolean;
  questionTarget?: DiagnosticQuestionTarget;
  questionComposition?: QuestionCompositionRequest;
  listType?: DiagnosticListQuestionListType;
  presentationMode?: DiagnosticListQuestionPresentationMode;
  listTitle?: string;
  showListTitle?: boolean;

  // front-end specific
  range?: string;
  choices?: DiagnosticQuestionOptionRequest[];
  showScore?: boolean;
  allowMediaSubmission?: boolean;
  slideStepBy?: number;
}

export interface QuestionCompositionRequest {
  questions: GroupQuestionQuestionsRequest[];
  options: GroupQuestionOptionsRequest[];
}

export interface GroupQuestionQuestionsRequest {
  uid?: string;
  questionRequest: GroupQuestionQuestionRequest;
}

interface GroupQuestionQuestionRequest {
  uid?: string;
  type: string;
  title: string;
  tags?: ResourceTag[] | string[];
}

interface GroupQuestionOptionsRequest {
  uid?: string;
  type: string;
  value: LanguageValue;
  score: number;
}

// TODO MA: check and remove if not needed
export interface DiagnosticQuestionOptionRequest extends DiagnosticQuestionOptionResponse {
  optionUid?: string;
  value?: LanguageValue;

  // front-end specific
  selected?: boolean;
}

export interface LanguageValue {
  languageCode: string;
  value: string;
}

export interface DiagnosticQuestionCopy {
  index: number;
  questionUid: string;
}

export interface DiagnosticQuestionFeedbackRequest {
  dynamicContentRequest: DynamicContentRequest[];
}

export interface FeedbackProviderSettings {
  allOrganizationMembers: boolean;
  privateOnly: boolean;
  selectedUserGroups: TargetGroupDetails[];
  selectedUsers: TargetUserDetails[];
  invitedUsers: string[];
  feedbackSelectionMode: FeedbackSelectionMode;
  feedbackQuestions?: DiagnosticQuestionResponse[];
}

export interface TargetUserDetails {
  _id: string;
  email: string;
  name: string;
}

export interface TargetGroupDetails {
  _id: string;
  name: string;
  type?: string;
  forceSubscribe?: boolean;
}

export interface FeedbackAudience {
  scope: FeedbackAudienceScope;
  userGroupUids?: TargetGroupDetails[];
  invitedUserUids?: TargetUserDetails[];
}

export enum FeedbackAudienceScope {
  Private = 'PRIVATE',
  InvitedMembers = 'INVITED_MEMBERS',
  SelectedGroups = 'SELECTED_GROUPS',
  AllMembers = 'ALL_MEMBERS',
}

export type DiagnosticQuestionType =
  | 'CHOICE_QUESTION'
  | 'INFO_QUESTION'
  | 'ORDERING_QUESTION'
  | 'SLIDER_QUESTION'
  | 'GRID_QUESTION'
  | 'MULTIPLE_CHOICE_QUESTION'
  | 'RATING_QUESTION'
  | 'TEXT_INPUT_QUESTION'
  | 'GROUP_QUESTION'
  | 'LIST_QUESTION'
  | 'DRAGDROP_QUESTION';

export type DiagnosticQuestionOptionType = 'CHOICE_OPTION' | 'SLIDER_OPTION' | 'GROUP_LIST_OPTION' | 'TEXT_LIST_OPTION';

export type DiagnosticListQuestionListType = 'GROUP_LIST' | 'TEXT_LIST';

export type DiagnosticListQuestionOptionType = 'GROUP_LIST_OPTION' | 'TEXT_LIST_OPTION';

export type DiagnosticListQuestionPresentationMode = 'CHECKBOX' | 'RADIO' | 'SELECTABLE_DROP_DOWN';

export interface LogicGoal {
  tagUid: string;
  scoredLevels: LogicGoalLevel[];
}

export interface LearningGoalIconRequest {
  imageName: string;
  contentLength: number;
}

export interface LogicGoalLevel {
  levelUid: string;
  scoreRangeMin: number;
  scoreRangeMax: number;
}

export interface DiagnosticLogic {
  tagUid: string;
  tagTitle: string;
  scoreRange: ScoreRange;
  scoredLevels: ScoredLevel[];
  icon: DiagnosticLogicIcon;
  numberOfQuestions: number;
  maxIcon: DiagnosticLogicIcon;
  minIcon: DiagnosticLogicIcon;
}

export interface DiagnosticGoalView {
  tagUid: string;
  tagTitle: string;
  levels: ScoredLevel[];
  dynamicContent?: EditorContent[];
  icon: DiagnosticLogicIcon;
  maxIcon: DiagnosticLogicIcon;
  minIcon: DiagnosticLogicIcon;
  minScore: number;
  maxScore: number;
}

export interface ScoreRange {
  min: number;
  max: number;
}

export interface ScoredLevel {
  levelUid: string;
  scoreRange: ScoreRange;
  competencyTitle: LanguageValue[];
  value?: LanguageValue;
}

export interface DiagnosticLogicIcon {
  iconClass: string;
  iconImageUrl: string;
}

export type QuestionResponsesStatus = 'IN_PROGRESS' | 'COMPLETED' | 'STARTED';

export interface QuestionResponses {
  uid: string;
  responses: QuestionResponse[];
  feedbackResponses: QuestionResponse[];
  version: number;
  status: QuestionResponsesStatus;
  compositeResponses?: QuestionResponse[];
}

export interface QuestionResponse {
  questionUid: string;
  timeTaken: number;
  selectedOptions: QuestionResponseSelectedOption[];
  evidence?: string;
  richTextAnswer?: EditorContent[];
  compositeResponses?: QuestionResponse[];
}

export interface QuestionResponseRequest {
  questionUid: string;
  duration: number;
  selectedOptions?: QuestionResponseSelectedOption[];
  feedbackFor?: string;
  evidence?: string;
  version: 1;
  richTextAnswer?: EditorContent[];
  compositeResponse?: QuestionResponseRequest[];
}

export interface SelectedFeedbackProvidersRequest {
  selectedFeedbackProviders: SelectedFeedbackProvider[];
  playlistUid: string;
}

export interface SelectedFeedbackProvider {
  feedbackProviderUserUid?: string;
  feedbackProviderEmail?: string;
  firstName?: string;
  lastName?: string;
}

export interface FeedbackProviderSettingRequest {
  allOrganizationMembers?: boolean;
  privateOnly?: boolean;
  selectedUserGroups?: string[];
  selectedUsers?: string[];
  invitedUsers?: string[];
  feedbackSelectionMode?: FeedbackSelectionMode;
}

export type FeedbackSelectionMode = 'MANUAL' | 'AUTOMATIC';

export interface FeedbackProviderUser {
  email: string;
  feedbackProvidedOn: Date;
  image: string;
  jobTitle: string;
  name: string;
  userUid: string;
  observationPeriod: string;
  observationStrength: string;
  organization: string;
  profileLink: string;
  relationship: string;
}

export interface GroupQuestionSelectedOption {
  question: DiagnosticFormattedQuestion;
  option: DiagnosticQuestionOptionRequest;
}

export interface QuestionResponseSelectedOption {
  optionUid: string;
  userScore: number;
}

export interface DiagnosticsRetake {
  type: DiagnosticsRetakeType;
  monthsAfter?: number;
}

export type DiagnosticsRetakeType = 'UNLIMITED' | 'MONTHS_AFTER' | 'NONE';

export interface AssessmentSummary {
  retake: boolean;
  lastVersion: number;
  completed: boolean;
  inProgress: boolean;
  questionResponseId?: string | null;
  feedbackSummary?: AssessmentFeedbackSummary;
}

export interface AssessmentFeedbackSummary {
  hasFeedback: boolean;
  feedbackFor: string;
}
